import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { APIService, BASEAPI } from 'src/api/api';
import API_URL from 'src/api/apiConstants';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import NewVehicle from 'src/components/vehicles/NewVehicle';

function UpdateOdometerModal({
  open,
  onClose,
  transactionId,
  currentValue,
  vehicleNumber,
  siteId,
  additionalEntry,
  assetCodeMappingEnabled,
  onSuccess
}) {
  const dispatch = useDispatch();

  const [odometer, setOdometer] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [additional, setAdditional] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehicleListLoading, setVehicleListLoading] = useState(false);
  const [vehicleListError, setVehicleListError] = useState('');
  const [vehicleSearchInput, setVehicleSearchInput] = useState('');
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);
  const [pendingNewVehicleNumber, setPendingNewVehicleNumber] = useState('');

  useEffect(() => {
    if (open) {
      setOdometer(currentValue ?? '');
      setVehicle(vehicleNumber ?? '');
      setAdditional(additionalEntry ?? '');
      setVehicleSearchInput(vehicleNumber ?? '');
      setPendingNewVehicleNumber('');
      setErrors({});
    }
  }, [open, currentValue, vehicleNumber, additionalEntry]);

  const loadVehicleOptions = useCallback(async () => {
    setVehicleListLoading(true);
    setVehicleListError('');
    try {
      const response = await BASEAPI.get(API_URL.allVehicles);
      const responseData = response?.data;
      let rawVehicleList = [];
      if (Array.isArray(responseData?.result?.results)) {
        rawVehicleList = responseData.result.results;
      } else if (Array.isArray(responseData?.result)) {
        rawVehicleList = responseData.result;
      } else if (Array.isArray(responseData?.results)) {
        rawVehicleList = responseData.results;
      } else if (Array.isArray(responseData)) {
        rawVehicleList = responseData;
      }

      const uniqueVehicleMap = new Map();
      rawVehicleList.forEach((item) => {
        const registrationNumber = item?.registration_num?.trim();
        if (registrationNumber) {
          uniqueVehicleMap.set(registrationNumber, {
            label: registrationNumber,
            value: registrationNumber
          });
        }
      });

      if (vehicleNumber && !uniqueVehicleMap.has(vehicleNumber)) {
        uniqueVehicleMap.set(vehicleNumber, {
          label: vehicleNumber,
          value: vehicleNumber
        });
      }

      setVehicleOptions(Array.from(uniqueVehicleMap.values()));
    } catch (error) {
      setVehicleOptions(vehicleNumber ? [{ label: vehicleNumber, value: vehicleNumber }] : []);
      setVehicleListError('Unable to load vehicles for this user.');
    } finally {
      setVehicleListLoading(false);
    }
  }, [vehicleNumber]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    loadVehicleOptions();

    return undefined;
  }, [open, loadVehicleOptions, assetCodeMappingEnabled]);

  const handleChange = (event) => {
    const { value } = event.target;
    if (value === '' || (/^\d+$/.test(value) && Number(value) >= 0)) {
      setOdometer(value);
      if (errors.odometer_entry) {
        setErrors((prev) => ({ ...prev, odometer_entry: '' }));
      }
    }
  };

  const handleVehicleChange = (_, selectedOption) => {
    setVehicle(selectedOption?.value || '');
    setVehicleSearchInput(selectedOption?.value || '');
    if (errors.vehicle_number) {
      setErrors((prev) => ({ ...prev, vehicle_number: '' }));
    }
  };

  const matchingVehicleOptions = useMemo(() => {
    const query = vehicleSearchInput.trim().toLowerCase();
    if (!query) {
      return vehicleOptions;
    }
    return vehicleOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [vehicleOptions, vehicleSearchInput]);

  const searchedVehicleNumber = vehicleSearchInput.trim().toUpperCase();
  const vehicleNumberForPrefill = pendingNewVehicleNumber || searchedVehicleNumber;

  const addVehiclePrefillValues = useMemo(() => ({
    registration_num: vehicleNumberForPrefill,
    type: 'Movable',
    tank_capacity: 500,
    asset_code: vehicleNumberForPrefill,
    site: siteId ?? '',
    fuel_type: 'Diesel',
    odometer_reading: 0
  }), [vehicleNumberForPrefill, siteId]);

  const handleVehicleCreated = async (createdVehicle) => {
    const createdVehicleNumber = createdVehicle?.registration_num || searchedVehicleNumber;
    setVehicle(createdVehicleNumber);
    setVehicleSearchInput(createdVehicleNumber);
    await loadVehicleOptions();
  };

  const renderNoVehicleState = () => (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        No vehicle matches found.
      </Typography>
      <Button
        variant="contained"
        size="small"
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={() => {
          setPendingNewVehicleNumber(searchedVehicleNumber);
          setIsAddVehicleModalOpen(true);
        }}
        disabled={!searchedVehicleNumber}
        color="primary"
      >
        Add Vehicle
      </Button>
    </Box>
  );

  const handleAdditionalChange = (event) => {
    setAdditional(event.target.value);
    if (errors.additional_entry) {
      setErrors((prev) => ({ ...prev, additional_entry: '' }));
    }
  };

  const validateFields = () => {
    const validationErrors = {};

    const trimmedOdometer = String(odometer).trim();
    if (trimmedOdometer === '') {
      validationErrors.odometer_entry = 'Odometer reading is required';
    } else if (!/^\d+(\.\d+)?$/.test(trimmedOdometer) || Number(trimmedOdometer) < 0) {
      validationErrors.odometer_entry = 'Odometer reading must be a non-negative number';
    }

    const trimmedVehicle = vehicle.trim();
    if (trimmedVehicle === '') {
      validationErrors.vehicle_number = 'Vehicle number is required';
    }

    if (assetCodeMappingEnabled) {
      const trimmedAdditional = additional.trim();

      if (trimmedAdditional === '') {
        validationErrors.additional_entry = 'Additional entry is required';
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        odometer_entry: Number(String(odometer).trim()),
        vehicle_number: vehicle.trim()
      };

      if (assetCodeMappingEnabled) {
        payload.additional_entry = additional.trim();
      }

      const response = await APIService.updateOutwardOdometer(transactionId, payload);
      const isExplicitFailure = response?.success === false
        || (response?.status && Number(response.status) >= 400);
      if (isExplicitFailure) {
        throw response;
      }

      dispatch(
        setSuccessInfo({
          successMsg: 'Transaction updated successfully'
        })
      );

      onSuccess();
      onClose();
    } catch (error) {
      const responseData = error?.data || error;
      if (responseData?.vehicle_number) {
        setErrors((prev) => ({
          ...prev,
          vehicle_number: 'Selected vehicle is not allowed for this user.'
        }));
        return;
      }
      dispatch(
        setErrorInfo({
          errorMsg: 'Failed to update transaction'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <NewVehicle
        open={isAddVehicleModalOpen}
        handleClose={() => setIsAddVehicleModalOpen(false)}
        prefillValues={addVehiclePrefillValues}
        onSuccess={handleVehicleCreated}
      />

      <DialogTitle>Update Transaction</DialogTitle>

      <DialogContent>
        <Autocomplete
          options={vehicleOptions}
          inputValue={vehicleSearchInput}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset' && !value) {
              return;
            }
            setVehicleSearchInput(value);
          }}
          value={vehicleOptions.find((option) => option.value === vehicle) || null}
          onChange={handleVehicleChange}
          disabled={vehicleListLoading}
          noOptionsText={renderNoVehicleState()}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          filterOptions={() => matchingVehicleOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vehicle Number"
              placeholder="Search vehicle number"
              fullWidth
              margin="normal"
              error={Boolean(errors.vehicle_number)}
              helperText={errors.vehicle_number || vehicleListError || ''}
            />
          )}
        />

        {assetCodeMappingEnabled && (
          <TextField
            label="Additional Entry"
            value={additional}
            onChange={handleAdditionalChange}
            fullWidth
            margin="normal"
            error={Boolean(errors.additional_entry)}
            helperText={errors.additional_entry}
          />
        )}

        <TextField
          label="Odometer Reading"
          type="number"
          value={odometer}
          onChange={handleChange}
          fullWidth
          margin="normal"
          inputProps={{ min: 0 }}
          error={Boolean(errors.odometer_entry)}
          helperText={errors.odometer_entry}
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (assetCodeMappingEnabled && vehicleListLoading)}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UpdateOdometerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transactionId: PropTypes.number.isRequired,
  currentValue: PropTypes.number,
  vehicleNumber: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  additionalEntry: PropTypes.string,
  assetCodeMappingEnabled: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired
};

UpdateOdometerModal.defaultProps = {
  currentValue: null,
  vehicleNumber: '',
  siteId: null,
  additionalEntry: '',
  assetCodeMappingEnabled: false
};

export default UpdateOdometerModal;
